import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppSidebar } from '@/components/layout/AppSidebar'
import { SidebarProvider, SidebarInset, useSidebar } from '@/components/ui/sidebar'
import { Menu } from 'lucide-react'
import { NotificationBell } from '@/components/NotificationBell'

interface MainLayoutProps {
  children: React.ReactNode
  showNavigation?: boolean
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children, showNavigation = true }) => {
  // The full menu needs a wide window; below 1280px it starts as an icon rail so the page keeps
  // its room, and the user's own choice on a wide window is remembered.
  const WIDE = '(min-width: 1280px)'
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches)
  const saved = () => {
    try {
      return localStorage.getItem('sidebar-state') !== 'closed'
    } catch {
      return true
    }
  }
  const [sidebarOpen, setSidebarOpen] = useState(() => (wide ? saved() : false))
  useEffect(() => {
    const mql = window.matchMedia(WIDE)
    const onChange = () => {
      setWide(mql.matches)
      // Crossing the breakpoint resets to what that width wants
      setSidebarOpen(mql.matches ? saved() : false)
    }
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  // Mobile header menu
  const MobileHeader = () => {
    const { toggleSidebar } = useSidebar()

    return (
      <button
        onClick={toggleSidebar}
        className="md:hidden h-10 w-10 rounded-md flex items-center justify-center text-foreground hover:bg-foreground/10"
      >
        <Menu className="h-6 w-6" />
      </button>
    )
  }

  return (
    <SidebarProvider
      open={sidebarOpen}
      onOpenChange={(isOpen) => {
        setSidebarOpen(isOpen)
        // Only a choice made on a wide window is the user's preference
        if (wide) {
          try {
            localStorage.setItem('sidebar-state', isOpen ? 'open' : 'closed')
          } catch {
            /* storage blocked */
          }
        }
      }}
    >
      <div className="flex min-h-screen w-full overflow-x-hidden">
        <div className="fixed inset-0 -z-10 bg-linear-to-br from-[#F7F9F3] via-[#F4F7F0] to-[#EDF3EA]"></div>
        <AppSidebar />
        <SidebarInset className="min-w-0 bg-transparent transition-all duration-300">
          {showNavigation && (
            <header className="h-16 border-b bg-card flex items-center justify-between px-4 sm:px-6 sticky top-0 z-50">
              <div className="flex items-center gap-3">
                <MobileHeader />
                <Link to="/" className="flex items-center">
                  <div className="h-8 rounded-md flex items-center justify-center">
                    <img
                      src="https://adthlete.us-iad-1.linodeobjects.com/public/athios-logo-black.png"
                      alt="Athios Logo"
                      className="h-7"
                    />
                  </div>
                </Link>
              </div>

              <div className="flex items-center gap-3">
                <NotificationBell />
              </div>
            </header>
          )}

          <main className="min-w-0 flex-1 pt-2 pb-8">{children}</main>

          {showNavigation && (
            <footer className="bg-transparent border-t border-foreground/10 py-4 mt-auto">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center text-sm text-gray-500">
                  © {new Date().getFullYear()} Athios. All rights reserved.
                </div>
              </div>
            </footer>
          )}
        </SidebarInset>
      </div>
    </SidebarProvider>
  )
}

export default MainLayout
